// SheetErrorBoundary — Error Boundary compartilhado pra conteúdo de Sheets.
//
// Extraído de TaskDetailSheet (P1.13) pra ser reusado em PipelineRunDetailSheet
// (W2.5): a Sheet de run QUEBRAVA A TELA ao abrir (sem boundary), enquanto a de
// task já estava protegida. Em vez de duplicar a classe, centralizamos aqui.
//
// Captura crashes de render da árvore de filhos. Crashes em event
// handlers/promises não passam por boundary React — quem precisar disso
// adiciona listeners window.error no componente consumidor (TaskDetailSheet faz).
//
// `resetKey`: quando muda (ex: trocou de task/run), limpa o estado de erro
// pra a nova entidade renderizar do zero (em vez de manter o erro da anterior).

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface SheetErrorBoundaryProps {
  children: ReactNode;
  /** Identificador da entidade aberta (taskId, runId…). Trocar reseta o erro. */
  resetKey: string | null;
  /** Prefixo de log pra identificar a origem no console (ex: "TaskDetailSheet"). */
  logLabel?: string;
}

interface SheetErrorBoundaryState {
  hasError: boolean;
  message: string;
  stack: string | null;
}

export class SheetErrorBoundary extends Component<SheetErrorBoundaryProps, SheetErrorBoundaryState> {
  state: SheetErrorBoundaryState = { hasError: false, message: '', stack: null };

  static getDerivedStateFromError(error: Error): SheetErrorBoundaryState {
    return {
      hasError: true,
      message: error.message || 'Erro inesperado',
      stack: error.stack ?? null,
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(`[${this.props.logLabel ?? 'SheetErrorBoundary'}] crash externo · key`, this.props.resetKey, error, info);
  }

  componentDidUpdate(prev: SheetErrorBoundaryProps): void {
    if (prev.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, message: '', stack: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col h-full p-6 gap-3">
          <div className="rounded-md border border-raios-danger/40 bg-raios-danger/5 p-4 text-sm">
            <p className="font-semibold text-raios-danger-text mb-2">
              Erro ao renderizar o detalhe.
            </p>
            <p className="text-raios-text-secondary text-xs mb-2 font-mono break-all">
              {this.state.message}
            </p>
            {this.state.stack && (
              <details className="text-[11px] text-raios-muted">
                <summary className="cursor-pointer">stack</summary>
                <pre className="mt-2 whitespace-pre-wrap font-mono text-[10px]">{this.state.stack}</pre>
              </details>
            )}
            <p className="text-raios-muted text-[11px] mt-3">
              O erro foi logado no console. Tenta fechar e abrir de novo, ou copia a mensagem acima.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
